<template>
  <span v-if="showBadge" :class="badgeClass" :title="tooltip" data-testid="pre-strike-badge">
    {{ label }}
  </span>
</template>

<script setup lang="ts">
// Interim pre-strike badge (PR A, 2026-09-16 gold/GGE strike-anchor arc,
// Task 1). Gold and GGE have no strike-anchor mechanism yet (unlike
// BGA/JH) so the TOTAL row's r_kt is a stale full-day daily figure after
// the fund's strike cutoff — see the 2026-08-20 "+8.66% catastrophic
// reporting" incident. This badge is a Vue-only mitigation shipped ahead
// of the multi-week backend arc that adds a real strike anchor; Tasks
// 12/13 remove it on cutover. BGA/JH render nothing — they already have
// proper strike-anchor coverage.
//
// Review fix round 1 (2026-09-16): the strike/day-roll cutoff is 12:00 UK
// LOCAL time (noon London), timezone-aware across BST/GMT — see
// docs/superpowers/specs/2026-09-16-gold-gge-strike-anchor-design.md:31.
// A hardcoded UTC-hour constant (the original `dayRollUtcHour` prop) is
// only correct for one half of the year, so the cutoff is now computed
// from `now` via Intl.DateTimeFormat with an explicit Europe/London
// timeZone — same approach DealingPlanCard.vue's nowUkMinutes already
// uses, so BST/GMT transitions are handled automatically rather than by a
// magic number the caller has to remember to flip twice a year.
import { computed } from 'vue'

const props = defineProps<{
  fund: 'GOLD' | 'GGE' | 'BGA' | 'JH'
  now: Date
}>()

const NOON_UK_MINUTES = 12 * 60

// UK wall-clock time as minutes-since-midnight (Europe/London, DST-aware).
// Falls back to raw UTC hour/minute if Intl throws (matches
// DealingPlanCard.vue's nowUkMinutes fallback).
function nowUkMinutes(at: Date): number {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/London',
      hourCycle: 'h23',
      hour: 'numeric',
      minute: 'numeric',
    })
    const parts = fmt.formatToParts(at)
    const hour = parseInt(parts.find(p => p.type === 'hour')?.value ?? '0', 10)
    const minute = parseInt(parts.find(p => p.type === 'minute')?.value ?? '0', 10)
    return hour * 60 + minute
  } catch {
    return at.getUTCHours() * 60 + at.getUTCMinutes()
  }
}

const showBadge = computed(() => props.fund === 'GOLD' || props.fund === 'GGE')

const isPreStrike = computed(() => nowUkMinutes(props.now) < NOON_UK_MINUTES)

// Short badge labels (mobile-first HARD RULE — matches this pane's own
// src-badge-note / tier-badge / src-badge-live idiom: a short label in the
// badge, full explanation in the title tooltip) with a full explanation in
// the hover tooltip.
const label = computed(() => (isPreStrike.value ? 'PRE-STRIKE' : 'POST-STRIKE'))

const tooltip = computed(() =>
  isPreStrike.value
    ? 'Pre-strike daily r_kt — includes overnight T-1 lag, see design bug 08-20'
    : 'Full-day daily r_kt (pre-strike snapshot — see hybrid footer for post-strike drift)'
)

const badgeClass = computed(() => (isPreStrike.value ? 'badge-amber' : 'badge-grey'))
</script>

<style scoped>
.badge-amber { background: #ffb84d; color: #000; padding: 2px 6px; border-radius: 3px; font-size: 0.75em; }
.badge-grey  { background: #ccc; color: #333; padding: 2px 6px; border-radius: 3px; font-size: 0.75em; }
</style>
